import React, { useState } from 'react';
import { signOutUser, trackApiCall } from '../utils/firebase';
import { getVariationsFromCache, saveVariationsToCache } from '../utils/firebase';
import { generateVariations } from '../utils/api';

const MAX_ITEMS = 16;

export default function SetupScreen({ user, onCreateChecklist }) {
  const [items, setItems] = useState([]);
  const [currentInput, setCurrentInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSignOut = async () => {
    try {
      await signOutUser();
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  };

  const addItem = () => {
    if (currentInput.trim() && items.length < MAX_ITEMS) {
      setItems([...items, currentInput.trim()]);
      setCurrentInput('');
    }
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addItem();
    }
  };

  const handleStartSpeech = async () => {
    if (items.length === 0) {
      alert('Please add at least one item to track.');
      return;
    }

    setIsProcessing(true);

    try {
      // Check cache for all items
      const cachedVariations = {};
      const uncachedPhrases = [];

      for (const item of items) {
        const cached = await getVariationsFromCache(item);
        if (cached) {
          cachedVariations[item] = cached;
        } else {
          uncachedPhrases.push(item);
        }
      }

      // If any phrases are uncached, make API call for ALL phrases
      if (uncachedPhrases.length > 0) {
        console.log(`Generating variations for ${uncachedPhrases.length} new phrases...`);
        console.log('Sending all phrases to optimize cache updates...');
        
        const allVariations = await generateVariations(items);

        // Save all variations to cache (updates existing + adds new)
        for (const [phrase, variants] of Object.entries(allVariations)) {
          await saveVariationsToCache(phrase, variants);
          cachedVariations[phrase] = variants;
        }

        console.log('All variations cached successfully');
        // Track API call for cost analysis
        if (user) {
          await trackApiCall(user.uid);
        }
      } else {
        console.log('All phrases found in cache - no API call needed!');
      }

      // Pass items and cached variations to active screen
      onCreateChecklist(items, cachedVariations);

    } catch (error) {
      console.error('Error setting up checklist:', error);
      alert('Failed to set up checklist. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const canStart = items.length > 0 && !isProcessing;

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <div className="max-w-4xl mx-auto p-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 text-transparent bg-clip-text">
              Add Your Key Points
            </h2>
            <p className="text-gray-400 mt-2">Welcome, {user?.displayName}</p>
          </div>
          <button onClick={handleSignOut} className="text-gray-400 hover:text-white transition">
            Sign Out
          </button>
        </div>

        <div className="bg-gray-800 rounded-2xl p-6 mb-6 border border-gray-700">
          <div className="flex gap-3 mb-4">
            <input
              type="text"
              value={currentInput}
              onChange={(e) => setCurrentInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Enter a key word or phrase..."
              className="flex-1 bg-gray-700 border-2 border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:border-purple-500 focus:outline-none"
              disabled={items.length >= MAX_ITEMS || isProcessing}
            />
            <button
              onClick={addItem}
              disabled={!currentInput.trim() || items.length >= MAX_ITEMS || isProcessing}
              className="bg-gradient-to-r from-purple-600 to-purple-700 px-6 py-3 rounded-xl font-semibold hover:from-purple-700 hover:to-purple-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              + Add
            </button>
          </div>
          
          <div className="text-sm text-gray-400 mb-4">
            {items.length}/{MAX_ITEMS} items • {MAX_ITEMS - items.length} remaining
          </div>

          {items.length > 0 && (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {items.map((item, index) => (
                <div key={index} className="bg-gray-700 rounded-lg px-4 py-3 flex justify-between items-center">
                  <span>{item}</span>
                  <button
                    onClick={() => removeItem(index)}
                    disabled={isProcessing}
                    className="text-red-400 hover:text-red-300 font-bold text-xl disabled:opacity-50"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <button
          onClick={handleStartSpeech}
          disabled={!canStart}
          className="w-full bg-gradient-to-r from-purple-600 to-pink-600 py-4 rounded-xl text-xl font-bold hover:from-purple-700 hover:to-pink-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isProcessing ? 'Preparing...' : 'Start Speech'}
        </button>

        {isProcessing && (
          <p className="text-center text-gray-400 mt-4">
            Generating speech variations... This may take a few seconds.
          </p>
        )}
      </div>
    </div>
  );
}